import * as FormulaJS from '@formulajs/formulajs'

/**
 * Excel-style in-cell formula evaluator for quote sheets.
 *
 * Supports numeric literals, string literals ("…" with "" escape),
 * column/parameter references (case-insensitive; bracket multi-word names),
 * arithmetic (+ - * / ^), text concatenation (&), comparisons, percent,
 * and FormulaJS-backed Excel functions such as SQRT, MIN, MAX, ABS and trig.
 *
 * Evaluates numbers in fixed-point Decimal arithmetic; callers round for
 * display only. Text results (CONCAT, IF branches, literals) are opt-in via
 * `allowText` so legacy numeric-only consumers keep their exact contract.
 * Keep the function catalog delegated so new Excel math functions do not need
 * one-off quote-sheet parser changes.
 * Returns structured errors (#REF / #ERR / #CIRC / #VALUE) instead of throwing.
 */

const SCALE = 1_000_000_000_000n
const SCALE_NUM = 1_000_000_000_000

export type SheetFormulaErrorCode = '#REF' | '#ERR' | '#CIRC' | '#VALUE'

export type SheetFormulaResult =
  | { ok: true; value: number | string }
  | { ok: false; code: SheetFormulaErrorCode; message: string }

/**
 * The legacy numeric-only result shape. Without `allowText: true` the
 * evaluator NEVER returns a string (text results raise #ERR), so callers on
 * the default contract keep `value: number` — see the overloads on
 * evaluateSheetFormula. This is what protects every pre-text consumer
 * (quote-sheet recalc, SheetFormulaCell) from the type widening.
 */
export type SheetFormulaNumericResult =
  | { ok: true; value: number }
  | { ok: false; code: SheetFormulaErrorCode; message: string }

export type SheetSymbolTable = Readonly<Record<string, number | string>>

export interface SheetFormulaEvalOptions {
  /** Reserved for cross-cell cycle detection during row recalc. */
  evalStack?: readonly string[]
  /**
   * Allow TEXT results (string literals, CONCAT, IF text branches).
   * Default false: a text result raises #ERR so legacy numeric-only
   * consumers (quote-sheet recalc) keep their exact contract.
   */
  allowText?: boolean
}

/** True when text is an Excel-style "=expression". */
export function isSheetFormula(text: string | null | undefined): boolean {
  return typeof text === 'string' && text.trim().startsWith('=')
}

export function stripSheetFormulaPrefix(raw: string): string {
  const trimmed = raw.trim()
  return trimmed.startsWith('=') ? trimmed.slice(1).trim() : trimmed
}

/** Blank out "…" string literals so their contents never read as refs. */
function maskStringLiterals(text: string): string {
  return text.replace(/"(?:[^"]|"")*"/g, (m) => ' '.repeat(m.length))
}

/** Extract dependency field keys / symbol names referenced by a formula. */
export function extractSheetFormulaRefs(expr: string): string[] {
  const text = maskStringLiterals(stripSheetFormulaPrefix(expr))
  const refs = new Set<string>()
  for (const m of text.matchAll(/\[([^\]]+)\]/g)) refs.add(normalizeSymbolKey(m[1]))
  const withoutBrackets = text.replace(/\[([^\]]+)\]/g, ' ')
  for (const m of withoutBrackets.matchAll(/[A-Za-z_][A-Za-z0-9_.]*/g)) {
    const upper = m[0].toUpperCase()
    const after = withoutBrackets.slice((m.index ?? 0) + m[0].length).trimStart()
    if (after.startsWith('(') || EXCEL_CONSTANTS.has(upper)) continue
    refs.add(normalizeSymbolKey(m[0]))
  }
  return [...refs]
}

// Overloads: text results only reach callers that OPT IN via allowText —
// everyone else keeps the exact legacy `value: number` contract.
export function evaluateSheetFormula(
  expr: string | null | undefined,
  symbols: SheetSymbolTable,
  options: SheetFormulaEvalOptions & { allowText: true },
): SheetFormulaResult
export function evaluateSheetFormula(
  expr: string | null | undefined,
  symbols: SheetSymbolTable,
  options?: SheetFormulaEvalOptions,
): SheetFormulaNumericResult
export function evaluateSheetFormula(
  expr: string | null | undefined,
  symbols: SheetSymbolTable,
  options: SheetFormulaEvalOptions = {},
): SheetFormulaResult {
  if (expr == null) return { ok: false, code: '#ERR', message: 'empty expression' }
  let text = String(expr).trim()
  if (!text) return { ok: false, code: '#ERR', message: 'empty expression' }
  if (text.startsWith('=')) text = text.slice(1).trim()
  if (!text) return { ok: false, code: '#ERR', message: 'empty expression' }

  const stack = options.evalStack ?? []

  try {
    const normalised = normaliseBracketRefs(text)
    const tokens = tokenize(normalised)
    const parser = new Parser(tokens)
    const ast = parser.parseExpression()
    parser.expectEnd()
    const value = evalAst(ast, symbols, stack)
    if (typeof value === 'string') {
      if (!options.allowText) {
        throw new SheetFormulaEvalError('#ERR', 'formula returned text where a number is required')
      }
      return { ok: true, value }
    }
    return { ok: true, value: decimalToNumber(value) }
  } catch (e) {
    if (e instanceof SheetFormulaEvalError) {
      return { ok: false, code: e.code, message: e.message }
    }
    return { ok: false, code: '#ERR', message: (e as Error).message || 'parse error' }
  }
}

/** Topological evaluation order; fields in `cycle` participate in a circular reference. */
export function orderSheetFormulaFields(
  fields: string[],
  formulas: Record<string, string>,
  _symbolsForField: (field: string) => SheetSymbolTable,
  aliasesForField: (field: string) => readonly string[] = (field) => [field],
): { order: string[]; cycle: Set<string> } {
  const deps = new Map<string, Set<string>>()
  for (const field of fields) {
    const expr = formulas[field]
    if (!expr || !isSheetFormula(expr)) {
      deps.set(field, new Set())
      continue
    }
    const refs = extractSheetFormulaRefs(expr)
    const fieldKeys = new Set(fields.map((f) => normalizeSymbolKey(f)))
    const mapped = new Set<string>()
    for (const ref of refs) {
      for (const f of fields) {
        const aliases = aliasesForField(f).map(normalizeSymbolKey)
        if (aliases.includes(ref) || normalizeSymbolKey(f) === ref) {
          mapped.add(f)
        }
      }
      if (fieldKeys.has(ref)) mapped.add(fields.find((f) => normalizeSymbolKey(f) === ref) ?? ref)
    }
    mapped.delete(field)
    deps.set(field, mapped)
  }

  const order: string[] = []
  const temp = new Set<string>()
  const perm = new Set<string>()
  const cycle = new Set<string>()

  const visit = (node: string) => {
    if (perm.has(node)) return
    if (temp.has(node)) {
      cycle.add(node)
      return
    }
    temp.add(node)
    for (const dep of deps.get(node) ?? []) visit(dep)
    temp.delete(node)
    perm.add(node)
    order.push(node)
  }
  for (const f of fields) visit(f)
  return { order, cycle }
}

// ---------------------------------------------------------------------------
// Decimal (fixed-point bigint)
// ---------------------------------------------------------------------------

type Decimal = bigint

function decimalFromNumber(n: number): Decimal {
  if (!Number.isFinite(n)) throw new SheetFormulaEvalError('#ERR', 'non-finite number')
  return BigInt(Math.round(n * SCALE_NUM))
}

function decimalFromLiteral(text: string): Decimal {
  const n = Number(text)
  if (!Number.isFinite(n)) throw new SheetFormulaEvalError('#ERR', `bad number '${text}'`)
  return decimalFromNumber(n)
}

function decimalToNumber(d: Decimal): number {
  return Number(d) / SCALE_NUM
}

function decimalAdd(a: Decimal, b: Decimal): Decimal {
  return a + b
}
function decimalSub(a: Decimal, b: Decimal): Decimal {
  return a - b
}
function decimalMul(a: Decimal, b: Decimal): Decimal {
  return (a * b) / SCALE
}
function decimalDiv(a: Decimal, b: Decimal): Decimal {
  if (b === 0n) throw new SheetFormulaEvalError('#ERR', 'division by zero')
  return (a * SCALE) / b
}
function decimalPow(a: Decimal, b: Decimal): Decimal {
  const base = decimalToNumber(a)
  const exp = decimalToNumber(b)
  return decimalFromNumber(Math.pow(base, exp))
}
function decimalNeg(a: Decimal): Decimal {
  return -a
}
function decimalPercent(a: Decimal): Decimal {
  return a / 100n
}

function normalizeSymbolKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, ' ')
}

function normaliseBracketRefs(text: string): string {
  return text.replace(/\[([^\]]+)\]/g, (_, inner: string) => `__BRK_${normalizeSymbolKey(inner).replace(/\s/g, '_')}__`)
}

function symbolToValue(v: number | string): Value {
  return typeof v === 'string' ? v : decimalFromNumber(v)
}

function resolveSymbol(name: string, symbols: SheetSymbolTable): Value {
  const upper = name.toUpperCase()
  if (upper === 'TRUE') return decimalFromNumber(1)
  if (upper === 'FALSE') return decimalFromNumber(0)

  const key = normalizeSymbolKey(name)
  if (key in symbols) return symbolToValue(symbols[key])
  const brkKey = `__brk_${key.replace(/\s/g, '_')}__`
  if (brkKey in symbols) return symbolToValue(symbols[brkKey])
  for (const [k, v] of Object.entries(symbols)) {
    if (normalizeSymbolKey(k) === key) return symbolToValue(v)
  }
  throw new SheetFormulaEvalError('#REF', `unknown reference '${name}'`)
}

// ---------------------------------------------------------------------------
// Tokenizer / Parser
// ---------------------------------------------------------------------------

type FormulaFunction = (...args: unknown[]) => unknown

const FORMULA_FUNCTIONS = Object.fromEntries(
  Object.entries(FormulaJS as Record<string, unknown>)
    .filter(([, value]) => typeof value === 'function')
    .map(([name, value]) => [name.toUpperCase(), value as FormulaFunction]),
)

const FUNCTION_ALIASES: Record<string, string> = {
  AVG: 'AVERAGE',
  CEIL: 'CEILING',
}

const EXCEL_CONSTANTS = new Set(['TRUE', 'FALSE'])

function normalizeFunctionName(name: string): string {
  const compact = name.toUpperCase().replace(/\./g, '')
  return FUNCTION_ALIASES[compact] ?? compact
}

function valueFromFormulaResult(result: unknown, fname: string): Value {
  if (result instanceof Error) {
    throw new SheetFormulaEvalError('#ERR', result.message || `${fname} returned an error`)
  }
  if (typeof result === 'boolean') return decimalFromNumber(result ? 1 : 0)
  if (typeof result === 'number') return decimalFromNumber(result)
  if (typeof result === 'string') {
    if (result.startsWith('#')) {
      throw new SheetFormulaEvalError('#ERR', `${fname} returned ${result}`)
    }
    return result
  }
  const n = Number(result)
  if (Number.isFinite(n)) return decimalFromNumber(n)
  throw new SheetFormulaEvalError('#ERR', `${fname} returned a non-numeric result`)
}

type TokenKind =
  | 'NUMBER'
  | 'STRING'
  | 'IDENT'
  | 'PLUS'
  | 'MINUS'
  | 'STAR'
  | 'SLASH'
  | 'CARET'
  | 'AMP'
  | 'LPAREN'
  | 'RPAREN'
  | 'COMMA'
  | 'PERCENT'
  | 'EQ'
  | 'NE'
  | 'LT'
  | 'LTE'
  | 'GT'
  | 'GTE'
  | 'EOF'

interface Token {
  kind: TokenKind
  text: string
  pos: number
}

function tokenize(src: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  while (i < src.length) {
    const c = src[i]
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i++
      continue
    }
    if (c === '<') {
      if (src[i + 1] === '=') {
        tokens.push({ kind: 'LTE', text: '<=', pos: i })
        i += 2
      } else if (src[i + 1] === '>') {
        tokens.push({ kind: 'NE', text: '<>', pos: i })
        i += 2
      } else {
        tokens.push({ kind: 'LT', text: '<', pos: i })
        i++
      }
      continue
    }
    if (c === '>') {
      if (src[i + 1] === '=') {
        tokens.push({ kind: 'GTE', text: '>=', pos: i })
        i += 2
      } else {
        tokens.push({ kind: 'GT', text: '>', pos: i })
        i++
      }
      continue
    }
    // Excel string literal: "…" with "" as the escaped quote.
    if (c === '"') {
      const start = i
      i++
      let out = ''
      let closed = false
      while (i < src.length) {
        if (src[i] === '"') {
          if (src[i + 1] === '"') {
            out += '"'
            i += 2
            continue
          }
          i++
          closed = true
          break
        }
        out += src[i]
        i++
      }
      if (!closed) throw new SyntaxError(`unterminated string at ${start}`)
      tokens.push({ kind: 'STRING', text: out, pos: start })
      continue
    }
    const single: Record<string, TokenKind> = {
      '+': 'PLUS',
      '-': 'MINUS',
      '*': 'STAR',
      '/': 'SLASH',
      '^': 'CARET',
      '&': 'AMP',
      '(': 'LPAREN',
      ')': 'RPAREN',
      ',': 'COMMA',
      '%': 'PERCENT',
      '=': 'EQ',
    }
    if (single[c]) {
      tokens.push({ kind: single[c], text: c, pos: i })
      i++
      continue
    }
    if ((c >= '0' && c <= '9') || c === '.') {
      const start = i
      let sawDigit = false
      while (i < src.length && src[i] >= '0' && src[i] <= '9') {
        i++
        sawDigit = true
      }
      if (src[i] === '.') {
        i++
        while (i < src.length && src[i] >= '0' && src[i] <= '9') {
          i++
          sawDigit = true
        }
      }
      if (!sawDigit) throw new SyntaxError(`bad number at ${start}`)
      tokens.push({ kind: 'NUMBER', text: src.slice(start, i), pos: start })
      continue
    }
    if ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_') {
      const start = i
      while (
        i < src.length &&
        ((src[i] >= 'a' && src[i] <= 'z') ||
          (src[i] >= 'A' && src[i] <= 'Z') ||
          (src[i] >= '0' && src[i] <= '9') ||
          src[i] === '_' ||
          src[i] === '.')
      ) {
        i++
      }
      tokens.push({ kind: 'IDENT', text: src.slice(start, i), pos: start })
      continue
    }
    throw new SyntaxError(`unexpected char '${c}' at ${i}`)
  }
  tokens.push({ kind: 'EOF', text: '', pos: src.length })
  return tokens
}

type Ast =
  | { type: 'num'; value: Decimal }
  | { type: 'str'; value: string }
  | { type: 'var'; name: string }
  | { type: 'unary'; op: '+' | '-' | '%'; expr: Ast }
  | { type: 'bin'; op: '+' | '-' | '*' | '/' | '^' | '&' | '=' | '<>' | '<' | '<=' | '>' | '>='; left: Ast; right: Ast }
  | { type: 'call'; name: string; args: Ast[] }

class Parser {
  private pos = 0
  private tokens: Token[]
  constructor(tokens: Token[]) {
    this.tokens = tokens
  }
  parseExpression(): Ast {
    return this.parseComparison()
  }
  private parseComparison(): Ast {
    let left = this.parseConcat()
    while (
      this.peek('EQ') ||
      this.peek('NE') ||
      this.peek('LT') ||
      this.peek('LTE') ||
      this.peek('GT') ||
      this.peek('GTE')
    ) {
      const kind = this.advance().kind
      const op =
        kind === 'EQ'
          ? '='
          : kind === 'NE'
            ? '<>'
            : kind === 'LT'
              ? '<'
              : kind === 'LTE'
                ? '<='
                : kind === 'GT'
                  ? '>'
                  : '>='
      left = { type: 'bin', op, left, right: this.parseConcat() }
    }
    return left
  }
  // Excel precedence: & binds looser than + - but tighter than comparisons.
  private parseConcat(): Ast {
    let left = this.parseAdditive()
    while (this.peek('AMP')) {
      this.advance()
      left = { type: 'bin', op: '&', left, right: this.parseAdditive() }
    }
    return left
  }
  private parseAdditive(): Ast {
    let left = this.parseTerm()
    while (this.peek('PLUS') || this.peek('MINUS')) {
      const op = this.advance().kind === 'PLUS' ? '+' : '-'
      left = { type: 'bin', op, left, right: this.parseTerm() }
    }
    return left
  }
  private parseTerm(): Ast {
    let left = this.parseFactor()
    while (this.peek('STAR') || this.peek('SLASH')) {
      const op = this.advance().kind === 'STAR' ? '*' : '/'
      left = { type: 'bin', op, left, right: this.parseFactor() }
    }
    return left
  }
  private parseFactor(): Ast {
    const left = this.parsePercent()
    if (this.peek('CARET')) {
      this.advance()
      return { type: 'bin', op: '^', left, right: this.parseFactor() }
    }
    return left
  }
  private parsePercent(): Ast {
    let expr = this.parseUnary()
    while (this.peek('PERCENT')) {
      this.advance()
      expr = { type: 'unary', op: '%', expr }
    }
    return expr
  }
  private parseUnary(): Ast {
    if (this.peek('PLUS')) {
      this.advance()
      return { type: 'unary', op: '+', expr: this.parseUnary() }
    }
    if (this.peek('MINUS')) {
      this.advance()
      return { type: 'unary', op: '-', expr: this.parseUnary() }
    }
    return this.parsePrimary()
  }
  private parsePrimary(): Ast {
    const tok = this.advance()
    if (tok.kind === 'NUMBER') return { type: 'num', value: decimalFromLiteral(tok.text) }
    if (tok.kind === 'STRING') return { type: 'str', value: tok.text }
    if (tok.kind === 'IDENT') {
      if (this.peek('LPAREN')) {
        this.advance()
        const args: Ast[] = []
        if (!this.peek('RPAREN')) {
          args.push(this.parseExpression())
          while (this.peek('COMMA')) {
            this.advance()
            args.push(this.parseExpression())
          }
        }
        this.expect('RPAREN')
        return { type: 'call', name: tok.text, args }
      }
      return { type: 'var', name: tok.text }
    }
    if (tok.kind === 'LPAREN') {
      const inner = this.parseExpression()
      this.expect('RPAREN')
      return inner
    }
    throw new SyntaxError(`unexpected token at ${tok.pos}`)
  }
  expectEnd(): void {
    if (this.peekTok().kind !== 'EOF') throw new SyntaxError('unexpected trailing tokens')
  }
  private peek(kind: TokenKind): boolean {
    return this.tokens[this.pos].kind === kind
  }
  private peekTok(): Token {
    return this.tokens[this.pos]
  }
  private advance(): Token {
    return this.tokens[this.pos++]
  }
  private expect(kind: TokenKind): Token {
    const tok = this.advance()
    if (tok.kind !== kind) throw new SyntaxError(`expected ${kind}`)
    return tok
  }
}

class SheetFormulaEvalError extends Error {
  code: SheetFormulaErrorCode
  constructor(code: SheetFormulaErrorCode, message: string) {
    super(message)
    this.code = code
  }
}

/** Runtime value: fixed-point Decimal for numbers, plain string for text. */
type Value = Decimal | string

/** Coerce a Value to Decimal — Excel-style: numeric strings convert, text errors. */
function toDecimal(v: Value, context: string): Decimal {
  if (typeof v !== 'string') return v
  const t = v.trim()
  if (t !== '') {
    const n = Number(t)
    if (Number.isFinite(n)) return decimalFromNumber(n)
  }
  throw new SheetFormulaEvalError('#VALUE', `${context} needs a number, got text '${v}'`)
}

/** Display form of a Value for & concatenation. */
function valueToText(v: Value): string {
  if (typeof v === 'string') return v
  const n = decimalToNumber(v)
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toPrecision(10)))
}

function compareValues(l: Value, r: Value): number {
  if (typeof l === 'string' || typeof r === 'string') {
    // Excel compares text case-insensitively; mixed text/number never equal.
    if (typeof l === 'string' && typeof r === 'string') {
      const a = l.toLowerCase()
      const b = r.toLowerCase()
      return a < b ? -1 : a > b ? 1 : 0
    }
    return typeof l === 'string' ? 1 : -1 // Excel: any text > any number
  }
  return l < r ? -1 : l > r ? 1 : 0
}

function evalAst(
  node: Ast,
  symbols: SheetSymbolTable,
  stack: readonly string[],
): Value {
  switch (node.type) {
    case 'num':
      return node.value
    case 'str':
      return node.value
    case 'var':
      return resolveSymbol(node.name, symbols)
    case 'unary': {
      const v = toDecimal(evalAst(node.expr, symbols, stack), 'unary operator')
      if (node.op === '-') return decimalNeg(v)
      if (node.op === '%') return decimalPercent(v)
      return v
    }
    case 'bin': {
      const l = evalAst(node.left, symbols, stack)
      const r = evalAst(node.right, symbols, stack)
      switch (node.op) {
        case '&':
          return valueToText(l) + valueToText(r)
        case '+':
          return decimalAdd(toDecimal(l, "'+'"), toDecimal(r, "'+'"))
        case '-':
          return decimalSub(toDecimal(l, "'-'"), toDecimal(r, "'-'"))
        case '*':
          return decimalMul(toDecimal(l, "'*'"), toDecimal(r, "'*'"))
        case '/':
          return decimalDiv(toDecimal(l, "'/'"), toDecimal(r, "'/'"))
        case '^':
          return decimalPow(toDecimal(l, "'^'"), toDecimal(r, "'^'"))
        case '=':
          return decimalFromNumber(compareValues(l, r) === 0 ? 1 : 0)
        case '<>':
          return decimalFromNumber(compareValues(l, r) !== 0 ? 1 : 0)
        case '<':
          return decimalFromNumber(compareValues(l, r) < 0 ? 1 : 0)
        case '<=':
          return decimalFromNumber(compareValues(l, r) <= 0 ? 1 : 0)
        case '>':
          return decimalFromNumber(compareValues(l, r) > 0 ? 1 : 0)
        case '>=':
          return decimalFromNumber(compareValues(l, r) >= 0 ? 1 : 0)
      }
      return 0n
    }
    case 'call': {
      const fname = normalizeFunctionName(node.name)
      const formulaFn = FORMULA_FUNCTIONS[fname]
      if (!formulaFn) {
        throw new SheetFormulaEvalError('#ERR', `unknown function '${node.name}'`)
      }
      if (fname === 'IF') {
        // Excel arity: IF(cond, then[, else]) — omitted else is FALSE (0).
        if (node.args.length !== 2 && node.args.length !== 3) {
          throw new SheetFormulaEvalError('#ERR', 'IF requires 2 or 3 arguments')
        }
        const cond = toDecimal(evalAst(node.args[0], symbols, stack), 'IF condition')
        if (cond !== 0n) return evalAst(node.args[1], symbols, stack)
        return node.args.length === 3
          ? evalAst(node.args[2], symbols, stack)
          : decimalFromNumber(0)
      }
      const args = node.args.map((a) => {
        const v = evalAst(a, symbols, stack)
        return typeof v === 'string' ? v : decimalToNumber(v)
      })
      return valueFromFormulaResult(formulaFn(...args), fname)
    }
  }
}
